import React, { useEffect, useRef, useState } from 'react';
import { ArrowDown, ArrowRight } from 'lucide-react';
import './index.scss';

import ArtworkCard from './ArtworkCard';
import ArtistGallery from './ArtistGallery';
// import ArcDiagram from '../ArcDiagram';

import { textContent } from '../../types/data.global';
import { Modal } from 'antd';

import RolesDiagram from './RolesDiagram';
import DataArtHero from './DataArtHero';


const AirbusRedesign: React.FC = () => {
  // the news column is as tall as the roles diagram beside it and scrolls; the hint shows while more is below
  const newsRef = useRef<HTMLDivElement>(null);
  const [moreNews, setMoreNews] = useState(false);
  useEffect(() => {
    const el = newsRef.current;
    if (!el) return;
    let raf = 0;
    const check = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => setMoreNews(el.scrollTop + el.clientHeight < el.scrollHeight - 8)); };
    check();
    el.addEventListener('scroll', check, { passive: true });
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => { cancelAnimationFrame(raf); el.removeEventListener('scroll', check); ro.disconnect(); };
  }, []);


  return (
    <div className='container'>
      {/* <ArcDiagram /> */}

      {/* <div className='header'>
        <div>
          <p>REDESIGN</p>
          <p>UX/UI</p>
        </div>
        <p>AIRBUS.COM</p>
        <div>
          <p>UPROCK</p>
          <p>2023</p>
        </div>
      </div> */}

      <DataArtHero />

  	  <div className='introduction'>
        <div className='introduction-text'>
      	  {/* "The question of whether this is art or science—or physics or network science—makes absolutely no sense. They are all part of the same journey of discovery." */}

      	  Human society is complex, with many issues that resist immediate change. 
      	  While much of my research leverages data to drive understanding and decision-making, I am deeply interested in scenarios where the focus is not on altering outcomes but on raising awareness and humanistic concerns. In this context, big data, AI, and visualization serve to foster reflection rather than action. When our autdience expands from scientists and decision-makers to the broader public, data-driven art becomes a powerful medium to uncover the ``grey areas'' of society, evoke emotions, and spark public dialogue. 
      	  {/* While my research utilizes data to enable understanding and decision-making, I am equally committed to raising awareness of humanistic concerns.  */}
      	  
      	  {/* Compared to purely humanistic methods, I believe data-driven expressions provide a rational and convincing foundation for presenting these phenomena.  */}
      	  
      	  {/* When audiences expand to the broader public, data-driven art becomes a powerful medium to spark public dialogue.  */}
        </div>
  	  </div>

      <div className='second-section-div'>
        <div className='featured-news-div'>
          <h2 className='sectionTitle'>FEATURED NEWS</h2>
          <div className='featuredNews'>
            {/* <img src="/api/placeholder/400/300" alt="Planet" style={{ width: '100%', height: 'auto' }} /> */}
            <div className='newsGrid' ref={newsRef} tabIndex={0} aria-label='Featured news (scrollable)'>
              {[
                { category: 'CONFERENCE', title: 'Our artwork "Effort", exploring human-AI collaboration in generative choreography, has been accepted to the NeurIPS 2026 Creative AI Track!', excerpt: 'Dec. 2026, Sydney, Australia.' },
                { category: 'CONFERENCE', title: 'I will present our latest working paper at the Data | Art 2025 Symposium in Harvard University!', excerpt: 'Jun. 11-12th, 2025, Boston, USA.' },
                { category: 'CONFERENCE', title: 'Two of our papers are accepted by ISEA2025: "Behind the Molecules: An Alternative Data Visualization to Critic Chemical Abuse in Food Production" and "Hidden Incidents of Animal Euthanasia: Exploring Kinship under Dominance and Affection".', excerpt: 'May 23-29th, 2025, Seoul, South Korea.' },
                { category: 'PANEL', title: 'I\'m organizing a VISAP 2024 panel "What Do Visualization Art Projects Bring to the VIS Community?" Welcome to join us!', excerpt: 'Wednesday, Oct 16th, 2024 @07:30–08:45am (CST), Online.' },
                { category: 'PANEL', title: 'I interviewed Amy Cesal, one of the co-founders from Data Visualization Society (DVS), and Daria Koshkina, a data visualization research specialist from The BarabasiLab, as our special guests for our VISAP 2024 panel "What Do Visualization Art Projects Bring to the VIS Community?"', excerpt: 'Sept. 2024, Online.' },
                { category: 'EXIHIBITION', title: 'We are now holding an exihibition "ALTERING NATURE: EXPLORING LIFE IN COMPUTATIONAL ART" starting from the late Oct. 2024', excerpt: 'Ping Yuan Andkinmay W Tang Gallery, HKUST, Hong Kong.', large: true }
              ].map((news, index) => (
                <div key={index} className={`newsCard ${news.large ? 'newsCardLarge' : ''}`}>
                  <p className='category'>{news.category}</p>
                  <p className='title'>{news.title}</p>
                  <p className='excerpt'>{news.excerpt}</p>
                  <button className='readMore'>
                    READ MORE <ArrowRight size={16} style={{ marginLeft: '4px' }} />
                  </button>
                </div>
              ))}
            </div>
            <div className={`news-scroll-hint${moreNews ? ' on' : ''}`} aria-hidden='true'>Scroll <ArrowDown size={14} /></div>
          </div>
        </div>
        <div className='roles-div'>
          <h2 className='sectionTitle'>MY ROLES</h2>
          <RolesDiagram />
        </div>
      </div>




      <h2 className='sectionTitle' id='philosophical-reflection'>PHILOSOPHICAL REFLECTION</h2>
      <div className="content-card">
        {textContent.dataArtPage.filter((artwork: any) => artwork.classification == 'PHILOSOPHICAL REFLECTION').map((artworkData: any, textIdx: number) =>
          <ArtworkCard key={artworkData.title} data={artworkData} textIdx={textIdx}/>
        )}
      </div>
      
      <h2 className='sectionTitle' id='protest-and-critique'>PROTEST AND CRITIQUE</h2>
      <div className="content-card">
        {textContent.dataArtPage.filter((artwork: any) => artwork.classification == 'PROTEST AND CRITIQUE').map((artworkData: any, textIdx: number) =>
          <ArtworkCard key={artworkData.title} data={artworkData} textIdx={textIdx} />
        )}
      </div>


      <h2 className='sectionTitle' id='humanity-appreciation'>HUMANITY APPRECIATION</h2>
      <div className="content-card">
        {textContent.dataArtPage.filter((artwork: any) => artwork.classification == 'HUMANITY APPRECIATION').map((artworkData: any, textIdx: number) =>
          <ArtworkCard key={artworkData.title} data={artworkData} textIdx={textIdx} />
        )}
      </div>

	  

      <h2 className='sectionTitle' id='artist-collaborator'>ARTIST COLLABORATOR</h2>
  	  <div className='aboutUs'>
  	  I'm fortunate to collaborate with artists from different fields. It's always fun to see how different perspectives and ideas can be combined to create something really cool.
  	  </div>
      <div className='aboutUs'>
        {/* ArtistGallery component is not defined, so we'll comment it out for now */}
        <ArtistGallery />

        {/* Uncomment and modify this section if needed
        {[
          'What we do',
          'Our worldwide presence',
          'Our products and services'
        ].map((text, index) => (
          <div key={index}>
            <p className='number'>{index + 1}</p>
            <p>{text}</p>
            <button className='discoverMore'>
              DISCOVER MORE <ArrowRight size={16} style={{ marginLeft: '4px' }} />
            </button>
          </div>
        ))} */}
      </div>
      <Modal
    title="Exhibition"
    open={false}
    onCancel={() => {}}
    footer={null}
  >
      <p>Exhibition</p>
    </Modal>
    
    </div>



  );
};

export default AirbusRedesign;
